/**
 * SignupPrompt Component
 * Shows after 10-second snippet ends for non-authenticated users
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Music, Headphones } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const SignupPrompt = ({ 
  visible, 
  onClose, 
  artistName, 
  trackTitle,
  onSuccess 
}) => {
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  
  const [mode, setMode] = useState('prompt'); // 'prompt', 'email', 'login'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await register(name, email, password);
      if (result.success) {
        onSuccess?.();
        onClose();
      } else {
        setError(result.error || 'Registration failed');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        onSuccess?.();
        onClose();
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    // For now, redirect to auth page with Google flow
    // In production, this would use Google OAuth directly
    navigate('/auth?provider=google');
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setName('');
    setError('');
    setMode('prompt');
  };

  return (
    <AnimatePresence>
      {visible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-md z-50"
            onClick={onClose}
          />

          {/* Modal Wrapper - flex centered container */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pb-20 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full max-w-md bg-[#0B0A10] rounded-3xl border border-white/10 overflow-hidden pointer-events-auto max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="signup-prompt"
            >
            {/* Close button */}
            <button
              onClick={() => { resetForm(); onClose(); }}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
              data-testid="close-signup-prompt"
            >
              <X className="w-4 h-4 text-white" />
            </button>

            {/* Content */}
            <div className="p-6 pt-12">
              {mode === 'prompt' && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center"
                >
                  {/* Icon */}
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#00F0FF] to-[#FF007F] flex items-center justify-center">
                    <Headphones className="w-10 h-10 text-white" />
                  </div>

                  {/* Message */}
                  <h2 
                    className="text-2xl font-bold text-white mb-2"
                    style={{ fontFamily: 'Unbounded' }}
                  >
                    Keep Listening
                  </h2>
                  <p className="text-[#A1A1AA] mb-2">
                    Support <span className="text-[#00F0FF] font-medium">{artistName || 'this artist'}</span>
                  </p>
                  <p className="text-[#52525B] text-sm mb-6">
                    Sign up free to unlock full tracks, save favorites, and connect with artists.
                  </p>

                  {/* Track info */}
                  {trackTitle && (
                    <div className="flex items-center justify-center gap-2 mb-6 px-4 py-2 bg-white/5 rounded-full">
                      <Music className="w-4 h-4 text-[#FF007F]" />
                      <span className="text-white text-sm truncate">{trackTitle}</span>
                    </div>
                  )}

                  {/* Signup Options */}
                  <div className="space-y-3">
                    {/* Google */}
                    <button
                      onClick={handleGoogleSignup}
                      className="w-full py-3 px-4 rounded-xl bg-white text-[#050505] font-medium flex items-center justify-center gap-3 hover:bg-white/90 transition-colors"
                      data-testid="google-signup-btn"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Continue with Google
                    </button>

                    {/* Email */}
                    <button
                      onClick={() => setMode('email')}
                      className="w-full py-3 px-4 rounded-xl bg-[#00F0FF] text-[#050505] font-medium flex items-center justify-center gap-3 hover:bg-[#00F0FF]/90 transition-colors"
                      data-testid="email-signup-btn"
                    >
                      <Mail className="w-5 h-5" />
                      Sign up with Email
                    </button>
                  </div>

                  {/* Login link */}
                  <p className="mt-6 text-[#A1A1AA] text-sm">
                    Already have an account?{' '}
                    <button 
                      onClick={() => setMode('login')}
                      className="text-[#00F0FF] hover:underline"
                    >
                      Log in
                    </button>
                  </p>

                  {/* Terms */}
                  <p className="mt-4 text-[#52525B] text-xs">
                    By signing up, you agree to our Terms of Service and Privacy Policy.
                    Your NLT Radio account also works on NoLiesTold.
                  </p>
                </motion.div>
              )}

              {mode === 'email' && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <button
                    onClick={() => setMode('prompt')}
                    className="text-[#A1A1AA] text-sm mb-4 hover:text-white transition-colors"
                  >
                    ← Back
                  </button>

                  <h2 
                    className="text-xl font-bold text-white mb-6"
                    style={{ fontFamily: 'Unbounded' }}
                  >
                    Create Account
                  </h2>

                  <form onSubmit={handleEmailSignup} className="space-y-4">
                    <div>
                      <label className="block text-[#A1A1AA] text-sm mb-2">Name</label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name"
                        required
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00F0FF] transition-colors"
                        data-testid="signup-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-[#A1A1AA] text-sm mb-2">Email</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        required
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00F0FF] transition-colors"
                        data-testid="signup-email-input"
                      />
                    </div>
                    <div>
                      <label className="block text-[#A1A1AA] text-sm mb-2">Password</label>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Create a password"
                        required
                        minLength={6}
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00F0FF] transition-colors"
                        data-testid="signup-password-input"
                      />
                    </div>

                    {error && (
                      <p className="text-[#EF4444] text-sm">{error}</p>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 px-4 rounded-xl bg-[#00F0FF] text-[#050505] font-medium hover:bg-[#00F0FF]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="submit-signup-btn"
                    >
                      {loading ? 'Creating account...' : 'Create Account'}
                    </button>
                  </form>

                  <p className="mt-4 text-center text-[#A1A1AA] text-sm">
                    Already have an account?{' '}
                    <button 
                      onClick={() => setMode('login')}
                      className="text-[#00F0FF] hover:underline"
                    >
                      Log in
                    </button>
                  </p>
                </motion.div>
              )}

              {mode === 'login' && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <button
                    onClick={() => setMode('prompt')}
                    className="text-[#A1A1AA] text-sm mb-4 hover:text-white transition-colors"
                  >
                    ← Back
                  </button>

                  <h2 
                    className="text-xl font-bold text-white mb-6"
                    style={{ fontFamily: 'Unbounded' }}
                  >
                    Welcome Back
                  </h2>

                  <form onSubmit={handleEmailLogin} className="space-y-4">
                    <div>
                      <label className="block text-[#A1A1AA] text-sm mb-2">Email</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        required
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00F0FF] transition-colors"
                        data-testid="login-email-input"
                      />
                    </div>
                    <div>
                      <label className="block text-[#A1A1AA] text-sm mb-2">Password</label>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Your password"
                        required
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00F0FF] transition-colors"
                        data-testid="login-password-input"
                      />
                    </div>

                    {error && (
                      <p className="text-[#EF4444] text-sm">{error}</p>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 px-4 rounded-xl bg-[#00F0FF] text-[#050505] font-medium hover:bg-[#00F0FF]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="submit-login-btn"
                    >
                      {loading ? 'Logging in...' : 'Log In'}
                    </button>
                  </form>

                  <p className="mt-4 text-center text-[#A1A1AA] text-sm">
                    Don't have an account?{' '}
                    <button 
                      onClick={() => setMode('email')}
                      className="text-[#00F0FF] hover:underline"
                    >
                      Sign up
                    </button>
                  </p>
                </motion.div>
              )}
            </div>
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SignupPrompt;
